import { useEffect, useState } from "react";
import { isApiError } from "@empite/api-client";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
} from "@empite/ui-components";
import type {
  DiscoveredModel,
  ModelCapability,
  ModelCapabilityInput,
  ModelCapabilityName,
  ModelDeprecationAdvice,
  NewProviderModel,
  Operator,
  Reason,
  ProviderModel,
  ProviderModelEdit,
  ProviderModelStatus,
} from "../admin-client";
import {
  useAvailableModels,
  useCreateProviderModel,
  useProviderAccounts,
  useModelUsage,
  useProviderModels,
  useRoutingProfileDetails,
  useRoutingProfiles,
  useSetProviderModelStatus,
  useUpdateProviderModel,
} from "../admin-hooks";
import { ErrorAlert, RequireOperator, can } from "./guards";
import { ReasonButton } from "./reason-prompt";
import { Pagination, StatusBadge, pageOf, usePaging, when } from "./common";
import { ModelTestPanel } from "./model-test";
import {
  compareUsage,
  reachableAliases,
  totalsByModel,
  usageKey,
} from "./model-usage";
import type {
  PolicyRow,
  SortDirection,
  UsageColumn,
  UsageTotals,
} from "./model-usage";

/** The permissions the Admin API guards this page with; repeated here only to
 *  decide what to draw. READ gates the usage read as well as the catalogue:
 *  the Guard writes every refusal to the audit trail, so a query fired for a
 *  permission the operator does not hold is a denial nobody chose to attempt. */
const WRITE = "providers:write";
const READ = "providers:read";
/** Adding, editing and deprecating a model is a write to the SHARED catalogue
 *  every tenant routes against, so the Admin API guards those three routes
 *  with model_catalogue:write — a PLATFORM-ONLY permission that AllowedInScope
 *  refuses to any tenant-confined subject whatever its roles say. Testing a
 *  model is still providers:write: it moves nothing. Drawing all four from one
 *  constant would offer a tenant_owner, who holds providers:write over its own
 *  provider accounts, an Add model button that answers 403 and writes an
 *  audited denial. */
const CATALOGUE_WRITE = "model_catalogue:write";

/**
 * 0027's length CHECK on routing.provider_models.deprecation_message, mirrored
 * so the operator hears the rule while typing rather than after confirming.
 *
 * Counted in CODE POINTS, never in `String.length`: the column's CHECK is
 * Postgres `length(text)`, the handler mirrors it with utf8.RuneCountInString,
 * and both count characters. JavaScript counts UTF-16 code units, so a message
 * carrying an emoji would be refused here at 511 characters that all three of
 * them accept.
 */
const MAX_DEPRECATION_MESSAGE = 512;

function messageLength(text: string): number {
  return [...text].length;
}

/**
 * The adapters this build carries, in the order internal/providers registers
 * them.
 *
 * Repeated here for the same reason WRITE is: to decide what to DRAW. The
 * server is the control — handleCreateModel resolves the submitted name
 * against providers.Providers() and refuses anything else with a 400 that
 * names the whole set — and this list only spares an operator from typing a
 * provider no adapter can serve and finding out on save. A row naming a
 * provider with no adapter is not merely unhelpful: routing would hand it to
 * a fallback chain that then fails to build an adapter for it, which is a 500
 * at request time in place of a refusal at the form.
 *
 * A free-text box was the alternative and is worse for exactly that reason.
 * A fetched list would be better still, but there is no route that publishes
 * the registry; if one is ever added, this constant is what it replaces.
 */
const PROVIDERS = ["anthropic", "openrouter", "openai", "gemini"] as const;

/**
 * The capability vocabulary, keyed by the union so the compiler notices when
 * the union grows.
 *
 * A Record rather than an array: adding a member to ModelCapabilityName (which
 * mirrors 0006's CHECK on routing.model_capabilities) makes this object fail
 * to typecheck until it is labelled, whereas an array of names would silently
 * go on offering six of seven and nobody would find the missing one from the
 * screen.
 */
const CAPABILITY_LABELS: Record<ModelCapabilityName, string> = {
  streaming: "Streaming",
  tools: "Tools",
  vision: "Vision",
  prompt_caching: "Prompt caching",
  reasoning: "Reasoning",
  structured_output: "Structured output",
  count_tokens: "Token counting",
};

const CAPABILITY_NAMES = Object.keys(
  CAPABILITY_LABELS,
) as ModelCapabilityName[];

/**
 * The three states a capability can be in, which is why this is not a
 * checkbox.
 *
 * "unrecorded" is the absence of a claim and "unsupported" is the claim "we
 * checked, this model cannot" — the API refuses an entry that does not choose
 * between the latter two, and drops an unrecorded capability from the array
 * entirely. A checkbox has two states and would have to fold two of these
 * three into one, which is how a model nobody has assessed gets recorded as
 * blind and then silently skipped by the resolver for every request carrying
 * an image.
 */
type CapabilityState = "unrecorded" | "supported" | "unsupported";

type CapabilitySet = Record<ModelCapabilityName, CapabilityState>;

const CAPABILITY_OPTIONS: Array<{ value: CapabilityState; label: string }> = [
  { value: "unrecorded", label: "Not recorded" },
  { value: "supported", label: "Supported" },
  { value: "unsupported", label: "Not supported" },
];

/** What each status change means, said once. Retiring is the one that stops
 *  requests, and an operator reaching for "deprecated" expecting that is the
 *  mistake these sentences exist to prevent. */
const STATUS_ACTIONS: Array<{
  status: ProviderModelStatus;
  label: string;
  danger: boolean;
  description: string;
}> = [
  {
    status: "available",
    label: "Make available",
    danger: false,
    description:
      "Routing may select this model again for the profiles that allow it. Any " +
      "deprecation message and replacement alias recorded against it are cleared: " +
      "there is nothing to move off, so there is nowhere to send anybody.",
  },
  {
    status: "deprecated",
    label: "Deprecate",
    danger: false,
    description:
      "The model keeps serving requests; it is marked as on its way out so nothing new is built on it.",
  },
  {
    status: "disabled",
    label: "Disable",
    danger: true,
    description:
      "The model stops answering immediately and can be switched back on at any time. " +
      "Callers are refused with the message and replacement recorded here, exactly as a " +
      "retired model refuses them — the difference is that this is a decision you expect " +
      "to undo, and nothing about the model's place in the world has changed.",
  },
  {
    status: "retired",
    label: "Retire",
    danger: true,
    description:
      "The model stops being selectable. Any routing profile that names it falls through to its next policy, or fails if it has none.",
  },
];

/**
 * How near a sunset has to be before the catalogue says so unprompted.
 *
 * Thirty days because that is roughly the window in which somebody can still
 * act — find who is on the model, tell them, move them — and because a banner
 * that is permanently lit is a banner nobody reads. A date further out is still
 * on the row; it is just not shouted about.
 */
const SUNSET_WARNING_DAYS = 30;

/**
 * Rows per page on the catalogue.
 *
 * Twenty-five because the table is twelve columns wide and every row carries a
 * badge list and up to five buttons — a hundred of those is a page nobody
 * scrolls to the end of, and the store's own ceiling is 500. It is deliberately
 * not configurable: a page-size control is one more thing to explain on a
 * screen whose job is to find one model.
 */
const MODELS_PER_PAGE = 25;

/**
 * Renders a stored sunset for an operator: the date, in their own timezone.
 *
 * The wire format is RFC 3339 in UTC because that is what callers receive, but
 * an operator reading a table wants the day they will be asked about, and the
 * console is the one surface where the reader's locale is known.
 */
function sunsetLabel(sunset: string): string {
  const at = new Date(sunset);
  return Number.isNaN(at.getTime())
    ? sunset
    : at.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

/** Whole days from now until a sunset; negative once it has passed. */
function daysUntil(sunset: string): number | undefined {
  const at = new Date(sunset);
  if (Number.isNaN(at.getTime())) {
    return undefined;
  }
  return Math.ceil((at.getTime() - Date.now()) / 86_400_000);
}

/**
 * The date box speaks `yyyy-mm-dd` and the API speaks RFC 3339, so the two
 * conversions live here rather than inline in the form.
 *
 * A date input cannot express a time, and asking an operator for one would be
 * false precision: nobody schedules a model's end for 14:30. The day is
 * therefore taken to mean MIDNIGHT UTC at its start, stated in the field's hint
 * so the operator knows which instant they picked, and the same instant is what
 * every caller is shown.
 */
function sunsetToInput(sunset: string | null): string {
  if (sunset === null) {
    return "";
  }
  const at = new Date(sunset);
  return Number.isNaN(at.getTime()) ? "" : at.toISOString().slice(0, 10);
}

function sunsetFromInput(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === "" ? undefined : `${trimmed}T00:00:00Z`;
}

/**
 * A ceiling the catalogue states, or the fact that it does not state one.
 *
 * Deliberately not the "no limit" a routing profile's null means: a missing
 * context window is a gap in what was recorded, not a promise that the model
 * has no ceiling, and an operator sizing a request off the second reading
 * gets a 400 from the provider.
 */
function stated(value: number | null): string {
  return value === null ? "not stated" : value.toLocaleString();
}

/**
 * Models — the catalogue routing chooses from.
 *
 * Capabilities are the column an operator actually reads: a profile that
 * allows tools against a model that does not support them is a request that
 * fails upstream, and this is where that mismatch is visible.
 */
export function Models() {
  return (
    <RequireOperator>
      {(session) => <ModelList operator={session.operator} />}
    </RequireOperator>
  );
}

/**
 * What the panel under the header is currently for: nothing, a new row, one
 * existing row, or one row's move down the retirement ladder.
 *
 * The third mode exists because deprecating is no longer one click: the columns
 * 0027 and 0041 added travel with the status, and they have to be COLLECTED
 * before the reason prompt rather than after it.
 */
type FormTarget =
  | { mode: "create" }
  | { mode: "edit"; row: ProviderModel }
  | { mode: "status"; row: ProviderModel; status: AdviceStatus }
  // The fourth mode changes nothing here. It shares the slot anyway, because
  // the panel is the same width in the same place and two open at once would
  // be two things claiming to be about one row.
  | { mode: "test"; row: ProviderModel }
  | null;

/**
 * The statuses that carry advice: every rung of the ladder except the top one.
 *
 * Derived from ProviderModelStatus rather than listed, so a fifth status added
 * to the union lands in the advice form by default. That is the safe direction
 * to fail — a new status that collects a message it did not need is a wasted
 * field, whereas one that silently skips the form would let an operator switch
 * a model off with no way to tell anybody where to go.
 *
 * A reinstate to `available` is refused with a 400 if it carries any advice, so
 * it stays the single button it has always been.
 */
type AdviceStatus = Exclude<ProviderModelStatus, "available">;

/** Which usage column the table is ordered by, if any. `null` is the
 *  catalogue's own order, which is what an operator gets back by clicking a
 *  third time — there is no way to un-sort a table that only toggles. */
type ModelSort = { column: UsageColumn; direction: SortDirection } | null;

function nextSort(current: ModelSort, column: UsageColumn): ModelSort {
  if (current === null || current.column !== column) {
    // Descending first: every question this column is opened for ("what is
    // still busy", "how many people would I have to tell") is a question about
    // the top of the list.
    return { column, direction: "desc" };
  }
  return current.direction === "desc" ? { column, direction: "asc" } : null;
}

/**
 * What the usage report can be, which is FOUR things and not two.
 *
 * The distinction the screen exists to protect is between a model with no
 * traffic and a model whose traffic this report does not carry, and there are
 * two ways to be the second: the read failed, or the read succeeded and said it
 * cut its own tail off. Rendering either as a dash would tell an operator that
 * nobody uses a model nobody measured, which is the one wrong answer a
 * deprecation screen must not give.
 */
type UsageReport =
  | { kind: "loading" }
  | { kind: "failed" }
  | {
      kind: "ready";
      truncated: boolean;
      days: number;
      since: string;
      until: string;
    };

function ModelList({ operator }: { operator: Operator }) {
  const models = useProviderModels();
  // The default window, which is the server's 30 days. Not sent explicitly:
  // the response states the window it used, and that is what the columns are
  // labelled from — asking for 30 and labelling from the request would put a
  // clamped answer under a heading that lies about it.
  const usage = useModelUsage({}, can(operator, READ));
  const setStatus = useSetProviderModelStatus();
  const [notice, setNotice] = useState<string | null>(null);
  const [target, setTarget] = useState<FormTarget>(null);
  const [sort, setSort] = useState<ModelSort>(null);
  /** "" is every model, which is what this page is FOR — the catalogue is
   *  platform-wide and the filter is a narrowing of it, never its default. */
  const [application, setApplication] = useState("");
  // Above the early returns below, with the screen's other state: the rows it
  // pages do not exist until after them, and a hook called below a return is
  // called on some renders and not others.
  const paging = usePaging(MODELS_PER_PAGE);
  const writable = can(operator, CATALOGUE_WRITE);
  // Kept apart from `writable` above: a subject may hold one without the other
  // in either direction — a tenant operator has providers:write and not the
  // catalogue, and nothing stops a future platform role from being the reverse.
  const testable = can(operator, WRITE);

  if (models.isPending) {
    return <Spinner label="Loading models" />;
  }
  if (models.error) {
    return (
      <ErrorAlert error={models.error} title="Could not load the models" />
    );
  }

  const catalogue = models.data?.models ?? [];
  const totals = totalsByModel(usage.data?.model_usage ?? []);
  const report: UsageReport = usage.data
    ? {
        kind: "ready",
        truncated: usage.data.truncated,
        days: usage.data.window.days,
        since: usage.data.window.since,
        until: usage.data.window.until,
      }
    : usage.error
      ? { kind: "failed" }
      : { kind: "loading" };
  const totalsFor = (row: ProviderModel): UsageTotals | undefined =>
    totals.get(usageKey(row.provider, row.model));
  // The application filter is applied HERE, over the rows already fetched,
  // rather than by re-reading the catalogue with ?application_id=. The whole
  // catalogue is one bounded read this page has already paid for, the row
  // carries the applications that permit it, and filtering locally means
  // switching applications is instant and cannot show a half-loaded table. The
  // server-side filter exists for callers that want only one application's rows
  // — the application screen does — and is not the right tool for a control
  // that is toggled while reading.
  const applicationNames = [
    ...new Set(catalogue.flatMap((row) => row.applications)),
  ].sort((a, b) => a.localeCompare(b));
  const visible =
    application === ""
      ? catalogue
      : catalogue.filter((row) => row.applications.includes(application));
  // Sorted out of place: `models.data` is react-query's cache and mutating it
  // would reorder every other reader of the same entry. Array.prototype.sort is
  // stable, so rows the comparator calls equal — every unmeasured one — keep
  // the catalogue's order among themselves.
  const rows =
    sort === null
      ? visible
      : [...visible].sort((a, b) =>
          compareUsage(totalsFor(a), totalsFor(b), sort.column, sort.direction),
        );
  // Paged AFTER the filter and the sort, so a page is a window onto the list
  // the operator is actually looking at rather than onto the catalogue's own
  // order. The whole catalogue is already in hand — one bounded read, capped at
  // 500 by the store — so this is a rendering decision and costs no request.
  const paged = pageOf(rows, paging);
  // "(30d)" only once the window is known. Labelling it from the default while
  // the read is still in flight would state a window the answer may not use.
  const windowSuffix = report.kind === "ready" ? ` (${report.days}d)` : "";

  // The warning half of the sunset feature: models whose end is close enough to
  // act on, surfaced without the operator having to go looking.
  //
  // A model whose date has already PASSED is included, and deliberately so — it
  // is the one that most needs attention, because it is still serving traffic
  // it told everybody it would not. Sorted soonest first, which puts those at
  // the top on their own.
  const expiring = visible
    .filter((row) => {
      const days =
        row.sunset_at === null ? undefined : daysUntil(row.sunset_at);
      return days !== undefined && days <= SUNSET_WARNING_DAYS;
    })
    .sort((a, b) => (a.sunset_at ?? "").localeCompare(b.sunset_at ?? ""));
  const overdue = expiring.filter(
    (row) => row.sunset_at !== null && (daysUntil(row.sunset_at) ?? 0) < 0,
  );

  return (
    <>
      <PageHeader
        title="Models"
        description="Every provider model the router knows about, what it can do, how much traffic it carries, and whether it may still be chosen. Models are added here platform-wide; which of them an application may call, and who has them yet, is set on that application."
        actions={
          writable ? (
            <Button
              variant="primary"
              onClick={() => {
                setNotice(null);
                setTarget({ mode: "create" });
              }}
            >
              Add model
            </Button>
          ) : null
        }
      />
      {notice ? <Alert tone="success" title={notice} /> : null}
      <Card>
        <ErrorAlert
          error={setStatus.error}
          title="That model could not be changed"
        />
        {applicationNames.length === 0 ? null : (
          <div className="esm-form-grid">
            <Field
              label="Application"
              htmlFor="model-application-filter"
              hint="Narrows the list to models some routing profile of that application permits. It does not change the catalogue — a model is in the catalogue whether or not anything routes to it."
            >
              <Select
                id="model-application-filter"
                value={application}
                onChange={(event) => setApplication(event.target.value)}
              >
                <option value="">Every application</option>
                {applicationNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        )}
        {/* The usage read failing is not the catalogue failing, so it is said
            here and the two columns still render — as "unknown", never as a
            dash. A column of dashes would read as "nothing uses any of these",
            which is the exact misreading the dash rule exists to prevent. */}
        <ErrorAlert
          error={usage.error}
          title="The usage figures could not be loaded"
        />
        {expiring.length === 0 ? null : (
          <Alert
            tone={overdue.length > 0 ? "danger" : "warning"}
            title={
              overdue.length > 0
                ? `${overdue.length} model${overdue.length === 1 ? " is" : "s are"} past the date callers were given`
                : `${expiring.length} model${expiring.length === 1 ? "" : "s"} stop answering within ${SUNSET_WARNING_DAYS} days`
            }
          >
            <ul>
              {expiring.map((row) => {
                const days =
                  row.sunset_at === null ? undefined : daysUntil(row.sunset_at);
                return (
                  <li key={row.id}>
                    <code>{row.model}</code> on <code>{row.provider}</code> —{" "}
                    {row.sunset_at === null ? null : sunsetLabel(row.sunset_at)}
                    {days === undefined
                      ? ""
                      : days < 0
                        ? ` (${-days} days ago, and still ${row.status})`
                        : days === 0
                          ? " (today)"
                          : ` (in ${days} days)`}
                    {row.replacement_alias === null ? (
                      ", with no replacement named"
                    ) : (
                      <>
                        , replaced by <code>{row.replacement_alias}</code>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          </Alert>
        )}
        {report.kind === "ready" && report.truncated ? (
          <Alert tone="info" title="The usage report was cut short">
            More models carry traffic than one report returns, and the tail it
            dropped is the quietest end of it. A model with no figures below is
            one this report does not cover: its traffic is small, but it is not
            necessarily nothing.
          </Alert>
        ) : null}
        {/* Keyed by what the form is for, so opening Edit on a second row
            rebuilds the state rather than leaving the first row's values in
            the boxes under a new heading. The status panel is keyed on the
            transition too: switching from Deprecate to Retire on one row is a
            different decision and must not inherit the other one's draft. */}
        {target && (target.mode === "test" ? testable : writable) ? (
          target.mode === "test" ? (
            <ModelTestPanel
              key={`test:${target.row.id}`}
              row={target.row}
              onClose={() => setTarget(null)}
            />
          ) : target.mode === "status" ? (
            <DeprecationForm
              key={`${target.row.id}:${target.status}`}
              row={target.row}
              status={target.status}
              totals={totalsFor(target.row)}
              report={report}
              onClose={() => setTarget(null)}
              onDone={(message) => {
                setTarget(null);
                setNotice(message);
              }}
            />
          ) : (
            <ModelForm
              key={target.mode === "create" ? "create" : target.row.id}
              target={target}
              onClose={() => setTarget(null)}
              onDone={(message) => {
                setTarget(null);
                setNotice(message);
              }}
            />
          )
        ) : null}
        <DataTable<ProviderModel>
          rows={paged.visible}
          rowKey={(row) => row.id}
          empty="No model has been catalogued on this deployment yet."
          columns={[
            {
              key: "model",
              header: "Model",
              // Three names, in the order somebody needs them: what operators
              // read, what CUSTOMERS send, and what the provider answers to.
              // The middle one is the connection between this page and the
              // application screens, which otherwise speak different
              // vocabularies with nothing linking them.
              render: (row) => (
                <>
                  <div>{row.display_name || row.model}</div>
                  <div>
                    <code>{row.alias}</code>
                  </div>
                  {row.alias === row.model ? null : (
                    <code className="esm-muted-text">{row.model}</code>
                  )}
                </>
              ),
            },
            {
              key: "provider",
              header: "Provider",
              render: (row) => <code>{row.provider}</code>,
            },
            {
              key: "family",
              header: "Family",
              render: (row) =>
                row.family ? (
                  row.family
                ) : (
                  <span className="esm-muted-text">—</span>
                ),
            },
            {
              key: "status",
              header: "Status",
              // The date sits under the badge rather than in a column of its
              // own, because it is only ever meaningful for a row that is on
              // its way out — a whole column would be empty for most of the
              // catalogue and would push the capabilities off the fold.
              render: (row) => (
                <>
                  <StatusBadge status={row.status} />
                  {row.sunset_at === null ? null : (
                    <div className="esm-muted-text">
                      until {sunsetLabel(row.sunset_at)}
                    </div>
                  )}
                </>
              ),
            },
            {
              key: "applications",
              header: "Used by",
              // Names and not a count: "3 applications" tells an operator
              // deciding whether to retire a model nothing they can act on,
              // and the list is short because a catalogue row is routed by a
              // handful of profiles at most.
              render: (row) =>
                row.applications.length === 0 ? (
                  <span className="esm-muted-text">no routing profile</span>
                ) : (
                  <div className="esm-badge-row">
                    {row.applications.map((name) => (
                      <Badge key={name} tone="neutral">
                        {name}
                      </Badge>
                    ))}
                  </div>
                ),
            },
            {
              key: "capabilities",
              header: "Capabilities",
              render: (row) => <Capabilities capabilities={row.capabilities} />,
            },
            {
              key: "users",
              header: (
                <SortHeader
                  label={`Users${windowSuffix}`}
                  column="users"
                  sort={sort}
                  onSort={setSort}
                  title={
                    report.kind === "ready"
                      ? `The sum of each day's distinct users, ${report.since} to ${report.until}. A person who came back on a second day is counted twice, so it is an upper bound on the people a deprecation notice would reach — not a headcount.`
                      : "The sum of each day's distinct users over the window — an upper bound on the people a deprecation notice would reach, not a headcount."
                  }
                />
              ),
              align: "end",
              render: (row) => (
                <UsageCell
                  report={report}
                  totals={totalsFor(row)}
                  column="users"
                />
              ),
            },
            {
              key: "requests",
              header: (
                <SortHeader
                  label={`Requests${windowSuffix}`}
                  column="requests"
                  sort={sort}
                  onSort={setSort}
                  title={
                    report.kind === "ready"
                      ? `Requests served by this catalogue row, ${report.since} to ${report.until}, summed over every alias that reached it.`
                      : "Requests served by this catalogue row over the window, summed over every alias that reached it."
                  }
                />
              ),
              align: "end",
              render: (row) => (
                <UsageCell
                  report={report}
                  totals={totalsFor(row)}
                  column="requests"
                />
              ),
            },
            {
              key: "context",
              header: "Context",
              align: "end",
              render: (row) => stated(row.context_window),
            },
            {
              key: "tokens",
              header: "Max in / out",
              align: "end",
              render: (row) =>
                `${stated(row.max_input_tokens)} / ${stated(row.max_output_tokens)}`,
            },
            {
              key: "created",
              header: "Catalogued",
              render: (row) => when(row.created_at),
            },
            {
              key: "actions",
              header: "",
              align: "end",
              render: (row) => {
                if (!writable && !testable) {
                  return null;
                }
                return (
                  <div className="esm-actions esm-actions--table">
                    {/* Opening the form is not a write, so it is a plain
                        button. The reason prompt sits on Save, where the
                        request actually is. */}
                    {writable ? (
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setNotice(null);
                          setTarget({ mode: "edit", row });
                        }}
                      >
                        Edit
                      </Button>
                    ) : null}
                    {/* Before the status buttons: an operator is far more
                        likely to test a model than to move it down the ladder,
                        and testing is the thing that should happen FIRST. */}
                    {testable ? (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setNotice(null);
                          setTarget({ mode: "test", row });
                        }}
                      >
                        Test…
                      </Button>
                    ) : null}
                    {(writable ? STATUS_ACTIONS : [])
                      .filter((action) => action.status !== row.status)
                      .map((action) => {
                        const status = action.status;
                        // A move DOWN the ladder collects advice first: 0027's
                        // two columns are written by this same request, so the
                        // deprecation message and the replacement alias have to
                        // exist before the reason prompt opens, not after it.
                        // Opening that panel is not a write, so it is a plain
                        // button; the reason prompt is still where the request
                        // is, one step further in.
                        if (status !== "available") {
                          return (
                            <Button
                              key={status}
                              variant={action.danger ? "danger" : "secondary"}
                              onClick={() => {
                                setNotice(null);
                                setTarget({ mode: "status", row, status });
                              }}
                            >
                              {action.label}…
                            </Button>
                          );
                        }
                        // Reinstating has nothing to collect — the API refuses
                        // advice on a move to available with a 400 — so it
                        // stays the one-click confirmation it has always been.
                        return (
                          <ReasonButton
                            key={status}
                            variant={action.danger ? "danger" : "secondary"}
                            busy={
                              setStatus.isPending &&
                              setStatus.variables?.modelID === row.id
                            }
                            mutation={{
                              title: `${action.label}: ${row.display_name || row.model}`,
                              description: (
                                <p>
                                  <code>{row.model}</code> on{" "}
                                  <code>{row.provider}</code>.{" "}
                                  {action.description}
                                </p>
                              ),
                              confirmLabel: action.label,
                              run: async (reason) => {
                                const result = await setStatus.mutateAsync({
                                  modelID: row.id,
                                  status,
                                  reason,
                                });
                                setNotice(
                                  `${row.display_name || row.model} is now ${result.status}.`,
                                );
                              },
                            }}
                          >
                            {action.label}
                          </ReasonButton>
                        );
                      })}
                  </div>
                );
              },
            },
          ]}
        />
        <Pagination paged={paged} noun="models" />
        <UsageFootnote report={report} />
      </Card>
    </>
  );
}

/**
 * A sortable column heading.
 *
 * A button inside the `<th>` rather than a sortable DataTable: the primitive is
 * shared with the other two portals and owns the header cell, and giving it a
 * sort protocol would change every table in the workspace for two columns on
 * one screen. `header` is already a ReactNode, and a button is phrasing content
 * a `<th>` may hold, so the whole mechanism fits inside what the primitive
 * already offers.
 *
 * `aria-sort` belongs on the `<th>`, which this cannot reach, so the state is
 * carried in the button's own accessible name instead — an arrow alone would be
 * decoration to anything not looking at it.
 */
function SortHeader({
  label,
  column,
  sort,
  onSort,
  title,
}: {
  label: string;
  column: UsageColumn;
  sort: ModelSort;
  onSort: (next: (current: ModelSort) => ModelSort) => void;
  title: string;
}) {
  const active = sort?.column === column ? sort.direction : null;
  const state =
    active === "desc"
      ? "sorted, highest first"
      : active === "asc"
        ? "sorted, lowest first"
        : "not sorted";
  return (
    <button
      type="button"
      className="esm-sort"
      title={title}
      aria-label={`${label} — ${state}. Click to change the order.`}
      onClick={() => onSort((current) => nextSort(current, column))}
    >
      <span>{label}</span>
      <span className="esm-sort__arrow" aria-hidden="true">
        {active === "desc" ? "\u25bc" : active === "asc" ? "\u25b2" : "\u21c5"}
      </span>
    </button>
  );
}

/**
 * One usage figure, in the four renders the report's four states deserve.
 *
 * The dash is reserved for the ONE state it can honestly describe: the report
 * arrived, it was complete, and this model is not in it — nothing reached this
 * model in the window. Everything else that is not a number says "unknown" in
 * as many words, because "we did not measure this" and "nobody used this" are
 * the two answers a deprecation decision must never confuse, and they are one
 * keystroke apart in consequence.
 */
function UsageCell({
  report,
  totals,
  column,
}: {
  report: UsageReport;
  totals: UsageTotals | undefined;
  column: UsageColumn;
}) {
  if (totals !== undefined) {
    const value = column === "users" ? totals.usersUpperBound : totals.requests;
    return <>{value.toLocaleString()}</>;
  }
  if (report.kind === "loading") {
    return (
      <span className="esm-muted-text" title="Still reading the usage report.">
        &hellip;
      </span>
    );
  }
  if (report.kind === "failed") {
    return (
      <span
        className="esm-not-recorded"
        title="The usage report did not load, so this model's traffic is unknown."
      >
        unknown
      </span>
    );
  }
  if (report.truncated) {
    return (
      <span
        className="esm-not-recorded"
        title="The usage report was cut short and this model was below the cut. Its traffic is small, but not necessarily nothing."
      >
        unknown
      </span>
    );
  }
  return (
    <span
      className="esm-muted-text"
      title={`No request reached this model between ${report.since} and ${report.until}.`}
    >
      &mdash;
    </span>
  );
}

/** What the two numbers actually are, said once under the table rather than
 *  four times in four tooltips. */
function UsageFootnote({ report }: { report: UsageReport }) {
  if (report.kind !== "ready") {
    return null;
  }
  return (
    <p className="esm-muted-text">
      Usage covers {report.days} days, {report.since} to {report.until}. The
      rollup only writes finished days, so today is never in it.{" "}
      <strong>Users</strong> is the sum of each day&rsquo;s distinct users —
      somebody who came back on a second day is in it twice — which makes it an
      upper bound on the people a deprecation notice would have to reach, not a
      headcount. A dash is no traffic in that window; &ldquo;unknown&rdquo; is a
      figure this report does not carry.
    </p>
  );
}

// --------------------------------------------------------------------------
// The create / edit form
// --------------------------------------------------------------------------

interface ModelFormState {
  provider: string;
  model: string;
  alias: string;
  display_name: string;
  family: string;
  context_window: string;
  max_input_tokens: string;
  max_output_tokens: string;
  capabilities: CapabilitySet;
}

function blankCapabilities(): CapabilitySet {
  return Object.fromEntries(
    CAPABILITY_NAMES.map((name) => [name, "unrecorded" as CapabilityState]),
  ) as CapabilitySet;
}

/** A ceiling the row does not state is an empty box, not a zero: the column is
 *  nullable and `0` is refused by 0006's `> 0` CHECK. */
function ceilingText(value: number | null): string {
  return value === null ? "" : String(value);
}

function initialForm(target: Exclude<FormTarget, null>): ModelFormState {
  if (target.mode === "create") {
    return {
      provider: PROVIDERS[0],
      model: "",
      alias: "",
      display_name: "",
      family: "",
      context_window: "",
      max_input_tokens: "",
      max_output_tokens: "",
      capabilities: blankCapabilities(),
    };
  }
  const row = target.row;
  const capabilities = blankCapabilities();
  // Only the capabilities the row actually carries are moved off "not
  // recorded" — the API sends a claim either way and omits the rest, so an
  // absent entry is precisely the unrecorded state.
  for (const capability of row.capabilities) {
    if (capability.capability in capabilities) {
      capabilities[capability.capability as ModelCapabilityName] =
        capability.supported ? "supported" : "unsupported";
    }
  }
  return {
    provider: row.provider,
    model: row.model,
    alias: row.alias,
    display_name: row.display_name,
    family: row.family,
    context_window: ceilingText(row.context_window),
    max_input_tokens: ceilingText(row.max_input_tokens),
    max_output_tokens: ceilingText(row.max_output_tokens),
    capabilities,
  };
}

/**
 * A ceiling box, checked here only so the operator hears the rule before the
 * request rather than after it. The server applies the same one.
 */
function ceilingError(value: string): string | undefined {
  const trimmed = value.trim();
  if (trimmed === "") {
    return undefined;
  }
  if (!/^\d+$/.test(trimmed)) {
    return "Whole tokens only — digits, no separators.";
  }
  return Number(trimmed) > 0
    ? undefined
    : "Leave it empty when it is not recorded, rather than 0.";
}

/** An empty box means "not recorded", which the API spells `null`. It is not
 *  the same request as omitting the key, which would leave the column alone —
 *  this form round trips the whole row, so it always says which. */
function ceilingValue(value: string): number | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : Number(trimmed);
}

/** Only the capabilities somebody has actually assessed travel. An unrecorded
 *  one is left OUT of the array; on an edit that is what withdraws a claim,
 *  because `capabilities` replaces the recorded set rather than merging. */
function capabilityInputs(set: CapabilitySet): ModelCapabilityInput[] {
  return CAPABILITY_NAMES.filter((name) => set[name] !== "unrecorded").map(
    (name) => ({
      capability: name,
      supported: set[name] === "supported",
    }),
  );
}

/**
 * The one form behind "Add model" and every row's "Edit".
 *
 * One component rather than two because the fields are the same fields, and
 * two copies would drift the first time a column is added — the create half
 * would grow it and the edit half would quietly stop round tripping it, which
 * reads to an operator as the console clearing a value they never touched.
 *
 * The edit half PATCHes the WHOLE row back, provider and model included. That
 * is the shape the API was built for: the two are the natural key that model
 * policies and the price book join on, so echoing them unchanged is a no-op,
 * and moving them is a 409 whose sentence names which half was aimed at. It
 * also means a save that changes nothing returns the row rather than the "this
 * edit changes nothing" 400 an empty PATCH earns.
 */
function ModelForm({
  target,
  onClose,
  onDone,
}: {
  target: Exclude<FormTarget, null>;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const create = useCreateProviderModel();
  const update = useUpdateProviderModel();
  const [form, setForm] = useState<ModelFormState>(() => initialForm(target));
  const creating = target.mode === "create";

  const set =
    (field: keyof Omit<ModelFormState, "capabilities">) => (value: string) =>
      setForm((current) => ({ ...current, [field]: value }));

  const errors = {
    context: ceilingError(form.context_window),
    input: ceilingError(form.max_input_tokens),
    output: ceilingError(form.max_output_tokens),
  };
  const ready =
    form.provider !== "" &&
    form.model.trim() !== "" &&
    form.display_name.trim() !== "" &&
    Object.values(errors).every((error) => error === undefined);

  const label = creating ? "Add model" : "Save changes";
  const name = form.display_name.trim() || form.model.trim() || "this model";

  /**
   * Takes a model off the picker and into the boxes.
   *
   * It fills only what the vendor STATED. A field the vendor was silent about
   * is left as the operator had it rather than cleared, because clearing would
   * throw away something they may have already typed — and it is never filled
   * with a zero, because a zero in a ceiling is a limit the resolver enforces
   * and no vendor asked for one.
   *
   * Capabilities move to `supported` and never to `unsupported`: a vendor
   * listing states what a model accepts and never that it cannot, and importing
   * silence as a denial would refuse requests on the strength of a field
   * nobody wrote.
   */
  function prefill(picked: DiscoveredModel) {
    setForm((current) => {
      const next: ModelFormState = {
        ...current,
        model: picked.model,
        // The alias follows the id only while the operator has not typed one.
        // Overwriting a name they already chose would undo it every time they
        // corrected the model id underneath.
        alias: current.alias.trim() === "" ? picked.model : current.alias,
        display_name: picked.display_name || picked.model,
        family: picked.family || current.family,
        capabilities: { ...current.capabilities },
      };
      if (picked.context_window !== null) {
        next.context_window = String(picked.context_window);
      }
      if (picked.max_input_tokens !== null) {
        next.max_input_tokens = String(picked.max_input_tokens);
      }
      if (picked.max_output_tokens !== null) {
        next.max_output_tokens = String(picked.max_output_tokens);
      }
      for (const capability of picked.capabilities) {
        if (capability in next.capabilities) {
          next.capabilities[capability as ModelCapabilityName] = "supported";
        }
      }
      return next;
    });
  }

  async function run(reason: Reason) {
    if (creating) {
      const body: NewProviderModel = {
        provider: form.provider,
        model: form.model.trim(),
        // Omitted when blank: the server defaults it to the model id, and
        // sending "" would be asking for a blank name rather than a default.
        alias: form.alias.trim() === "" ? undefined : form.alias.trim(),
        display_name: form.display_name.trim(),
        // Omitted rather than sent empty: the column is nullable and the
        // server's NULLIF would make the two identical anyway, but a create
        // that never mentions a family is the honest description of one.
        family: form.family.trim() === "" ? undefined : form.family.trim(),
        context_window: ceilingValue(form.context_window),
        max_input_tokens: ceilingValue(form.max_input_tokens),
        max_output_tokens: ceilingValue(form.max_output_tokens),
        capabilities: capabilityInputs(form.capabilities),
      };
      const result = await create.mutateAsync({ body, reason });
      onDone(
        `Catalogued ${result.model.display_name} as ${result.model.model} on ${result.model.provider}.`,
      );
      return;
    }
    const body: ProviderModelEdit = {
      // Echoed from the ROW, never from an input: they are immutable, so
      // there is no box to move them in and nothing here can ask to.
      provider: target.row.provider,
      model: target.row.model,
      alias: form.alias.trim(),
      display_name: form.display_name.trim(),
      // Sent even when empty, because empty is how a family is CLEARED.
      family: form.family.trim(),
      context_window: ceilingValue(form.context_window),
      max_input_tokens: ceilingValue(form.max_input_tokens),
      max_output_tokens: ceilingValue(form.max_output_tokens),
      capabilities: capabilityInputs(form.capabilities),
    };
    const result = await update.mutateAsync({
      modelID: target.row.id,
      body,
      reason,
    });
    onDone(`Updated ${result.model.display_name} on ${result.model.provider}.`);
  }

  return (
    <div className="esm-subform">
      <h3>
        {creating
          ? "Add a model to the catalogue"
          : `Edit ${target.row.display_name}`}
      </h3>
      <p className="esm-muted-text">
        {creating
          ? "A new row is born available. It is not routable to until a routing profile carries a model policy naming it, and it cannot be reserved against until the price book carries a version for the same (provider, model) pair."
          : "The provider and the model id are fixed: they are the natural key that model policies and the price book both join on. Everything else here is a correction to what was recorded."}
      </p>
      {/* Whatever the API said, verbatim. A 409 here is a sentence explaining
          which rule was hit — that this provider already serves a model with
          that id, or which half of the natural key was aimed at — and
          replacing it with a house failure banner would throw away the only
          part of the answer that tells the operator what to do next. */}
      <ErrorAlert
        error={create.error ?? update.error}
        title={
          creating
            ? "That model was not catalogued"
            : "That change was not saved"
        }
      />
      {/* Only on a create. The provider and the model id are immutable on an
          edit, so a picker there would offer to change the one pair that
          cannot move. */}
      {creating ? (
        <ModelPicker provider={form.provider} onPick={prefill} />
      ) : null}
      {/* Not a <form>: the write goes through the reason prompt rather than a
          submit, and an Enter key that posted straight past the prompt would
          be a mutation with no reason on it. */}
      <div className="esm-form-grid">
        <Field
          label="Provider"
          htmlFor="model-provider"
          hint={
            creating
              ? "One of the adapters this build carries."
              : "Fixed once the row exists."
          }
        >
          <Select
            id="model-provider"
            value={form.provider}
            disabled={!creating}
            onChange={(event) => set("provider")(event.target.value)}
          >
            {PROVIDERS.map((provider) => (
              <option key={provider} value={provider}>
                {provider}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Model id"
          htmlFor="model-id"
          hint={
            creating
              ? "Exactly as the provider spells it — this is what goes on the wire."
              : "Fixed once the row exists."
          }
        >
          <Input
            id="model-id"
            value={form.model}
            readOnly={!creating}
            spellCheck={false}
            onChange={(event) => set("model")(event.target.value)}
          />
        </Field>
        <Field
          label="Customers call it"
          htmlFor="model-alias"
          hint={
            creating
              ? "The name clients send for this model. Leave it to match the provider's id, which is right for most models — change it when the id carries a release date, a -preview suffix or a vendor namespace that customers should not see."
              : "Editable only while no application serves it. Once a routing profile carries this name, customers are sending it and renaming it would refuse their next request."
          }
        >
          <Input
            id="model-alias"
            value={form.alias}
            spellCheck={false}
            placeholder={creating ? form.model : undefined}
            onChange={(event) => set("alias")(event.target.value)}
          />
        </Field>
        <Field
          label="Display name"
          htmlFor="model-display-name"
          hint="What operators read."
        >
          <Input
            id="model-display-name"
            value={form.display_name}
            onChange={(event) => set("display_name")(event.target.value)}
          />
        </Field>
        <Field
          label="Family"
          htmlFor="model-family"
          hint="Optional. Empty clears it."
        >
          <Input
            id="model-family"
            value={form.family}
            onChange={(event) => set("family")(event.target.value)}
          />
        </Field>
        <Field
          label="Context window"
          htmlFor="model-context-window"
          hint="Tokens. Empty means the catalogue does not state one."
          error={errors.context}
        >
          <Input
            id="model-context-window"
            inputMode="numeric"
            value={form.context_window}
            onChange={(event) => set("context_window")(event.target.value)}
          />
        </Field>
        <Field
          label="Max input tokens"
          htmlFor="model-max-input"
          error={errors.input}
        >
          <Input
            id="model-max-input"
            inputMode="numeric"
            value={form.max_input_tokens}
            onChange={(event) => set("max_input_tokens")(event.target.value)}
          />
        </Field>
        <Field
          label="Max output tokens"
          htmlFor="model-max-output"
          error={errors.output}
        >
          <Input
            id="model-max-output"
            inputMode="numeric"
            value={form.max_output_tokens}
            onChange={(event) => set("max_output_tokens")(event.target.value)}
          />
        </Field>
      </div>
      <h3>Capabilities</h3>
      <p className="esm-muted-text">
        Three states, not two. <strong>Not recorded</strong> is silence — nobody
        has assessed it — and <strong>Not supported</strong> is the claim that
        it was assessed and the model cannot. The router acts on the second and
        not on the first, so recording a guess as &ldquo;not supported&rdquo;
        takes the model out of every route that needs it.
        {creating
          ? null
          : " Setting one back to Not recorded withdraws the claim entirely."}
      </p>
      <div className="esm-form-grid">
        {CAPABILITY_NAMES.map((capability) => (
          <Field
            key={capability}
            label={CAPABILITY_LABELS[capability]}
            htmlFor={`model-capability-${capability}`}
          >
            <Select
              id={`model-capability-${capability}`}
              value={form.capabilities[capability]}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  capabilities: {
                    ...current.capabilities,
                    [capability]: event.target.value as CapabilityState,
                  },
                }))
              }
            >
              {CAPABILITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
        ))}
      </div>
      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={create.isPending || update.isPending}
          disabled={!ready}
          mutation={{
            title: creating ? `Catalogue ${name}` : `Save changes to ${name}`,
            tone: "primary",
            description: creating ? (
              <p>
                A row for <code>{form.model.trim() || "this model"}</code> on{" "}
                <code>{form.provider}</code>, available from the moment it is
                written. It changes no traffic on its own: routing only reaches
                a model a profile's policies name.
              </p>
            ) : (
              <p>
                <code>{target.row.model}</code> on{" "}
                <code>{target.row.provider}</code>. Capabilities are REPLACED by
                what is set above, so anything left at &ldquo;not
                recorded&rdquo; stops being recorded at all — and the router
                reads capabilities when it decides whether this model may serve
                a request.
              </p>
            ),
            confirmLabel: label,
            run,
          }}
        >
          {label}
        </ReasonButton>
        <Button
          variant="ghost"
          disabled={create.isPending || update.isPending}
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * Capabilities, with "not supported" shown as loudly as "supported".
 *
 * A capability the catalogue says nothing about simply has no badge — that is
 * silence, and it means "we do not know". `supported: false` is a claim, and
 * it gets a badge that says "no vision" in as many words. Rendering the two
 * the same way would let an operator read a gap in the catalogue as a
 * documented limitation, which is how a model gets excluded from a profile
 * for a restriction nobody ever asserted.
 */
function Capabilities({ capabilities }: { capabilities: ModelCapability[] }) {
  if (capabilities.length === 0) {
    return <span className="esm-muted-text">nothing catalogued</span>;
  }
  return (
    <div className="esm-badge-row">
      {capabilities.map((capability) => (
        <Badge
          key={capability.capability}
          tone={capability.supported ? "success" : "danger"}
        >
          {capability.supported
            ? capability.capability
            : `no ${capability.capability}`}
        </Badge>
      ))}
    </div>
  );
}

// --------------------------------------------------------------------------
// The deprecation panel
// --------------------------------------------------------------------------

/** The three `problem` extension members handleSetModelStatus attaches to a
 *  replacement refusal. Keyed on, never on the sentence: three different 409s
 *  reach this screen and only these three are about the alias box. */
const REPLACEMENT_PROBLEMS = [
  "model_unrouted",
  "replacement_not_permitted",
  "replacement_circular",
] as const;

/**
 * The refusal's own sentence, if the refusal was about the replacement alias.
 *
 * All three carry `replacement_alias` and a `problem` naming which rule they
 * hit, and all three have a different fix — edit a profile, name another alias,
 * point the alias at another catalogue row. That is why the server's sentence
 * is shown verbatim against the box instead of a house one: it is the only part
 * of the answer that says what to do next. The cast is because Problem declares
 * the RFC 9457 members and extensions are, by definition, not among them.
 */
function replacementRefusal(error: unknown): string | undefined {
  if (!isApiError(error) || error.code !== "conflict" || !error.problem) {
    return undefined;
  }
  const members = error.problem as unknown as Record<string, unknown>;
  const problem = members["problem"];
  if (
    typeof problem !== "string" ||
    !(REPLACEMENT_PROBLEMS as readonly string[]).includes(problem)
  ) {
    return undefined;
  }
  return typeof members["replacement_alias"] === "string"
    ? error.message
    : undefined;
}

/** The traffic sentence, in the four versions the report's four states allow.
 *  Shared by the panel and the confirmation so the number the operator reads
 *  while deciding is the number in front of them when they confirm. */
function usageSentence(
  report: UsageReport,
  totals: UsageTotals | undefined,
): string {
  if (report.kind === "loading") {
    return "The usage figures are still loading, so there is no traffic number behind this yet.";
  }
  if (report.kind === "failed") {
    return "The usage figures could not be loaded, so this decision is being made without them.";
  }
  if (totals !== undefined) {
    return (
      `Over the ${report.days} days to ${report.until} this model served ` +
      `${totals.requests.toLocaleString()} requests for at most ` +
      `${totals.usersUpperBound.toLocaleString()} people.`
    );
  }
  if (report.truncated) {
    return (
      `The usage report for the ${report.days} days to ${report.until} was cut short and this ` +
      "model was below the cut, so its traffic is small but not necessarily nothing."
    );
  }
  return (
    `No request reached this model in the ${report.days} days to ${report.until}, so there is ` +
    "nobody to move."
  );
}

/**
 * Deprecating or retiring one model, with the number the decision turns on and
 * the two things callers are owed.
 *
 * An inline panel rather than a modal, matching every other write form in this
 * console: the reason prompt is the only dialog the portal has, and a second one
 * underneath it would stack two backdrops on the way to one request. The panel
 * collects, the reason prompt confirms, and the confirmation repeats the traffic
 * number so it is in front of the operator at the moment of the decision rather
 * than a scroll away.
 *
 * The advice is PRE-FILLED from the row, and that is a correctness requirement
 * rather than a convenience: the status route writes both columns on every call,
 * so moving an already-deprecated model to retired without re-stating its
 * message would silently erase the sentence its callers are being shown. That
 * is not an inference — it is asserted, in as many words, by "retiring carries
 * the advice too" in internal/admin/model_deprecation_test.go: an unsent field
 * is not an unchanged one.
 *
 * The routing-profile reads it makes are safe without their own permission
 * gate: the panel only renders for an operator holding providers:write, and
 * every role in rbac.go that holds it holds providers:read too.
 */
function DeprecationForm({
  row,
  status,
  totals,
  report,
  onClose,
  onDone,
}: {
  row: ProviderModel;
  status: AdviceStatus;
  totals: UsageTotals | undefined;
  report: UsageReport;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const setStatus = useSetProviderModelStatus();
  const profiles = useRoutingProfiles();
  const details = useRoutingProfileDetails(
    (profiles.data?.routing_profiles ?? []).map((profile) => profile.id),
  );
  const [message, setMessage] = useState(row.deprecation_message ?? "");
  const [alias, setAlias] = useState(row.replacement_alias ?? "");
  const [sunset, setSunset] = useState(sunsetToInput(row.sunset_at));
  // The failure is held here rather than read off the mutation because `run`
  // swallows it — see the comment on run below.
  const [failure, setFailure] = useState<unknown>(null);

  const action = STATUS_ACTIONS.find((entry) => entry.status === status);
  const label = action?.label ?? status;
  const name = row.display_name || row.model;

  const vocabularyPending =
    profiles.isPending || details.some((detail) => detail.isPending);
  const vocabularyError =
    profiles.error ?? details.find((detail) => detail.error)?.error ?? null;
  const policies: PolicyRow[] = [];
  for (const detail of details) {
    const data = detail.data;
    if (!data) {
      continue;
    }
    for (const policy of data.model_policies) {
      policies.push({
        profileID: data.routing_profile.id,
        model: policy.model,
        provider: policy.provider,
        providerModel: policy.provider_model,
        allowed: policy.allowed,
      });
    }
  }
  const vocabularyReady = !vocabularyPending && vocabularyError === null;
  // Only a COMPLETE set of policies is asked the reachability question. A
  // half-loaded one answers it wrongly in both directions — the set of profiles
  // that permit this model grows with every profile that arrives, and so does
  // the set of rows serving each alias — and an offer that disappears from the
  // picker as the last request lands is worse than one that was never there.
  const reachable = vocabularyReady
    ? reachableAliases(policies, row.provider, row.model)
    : { routed: false, aliases: [] };
  // What the row already records stays on offer even when the profiles have
  // moved on since it was written, or could not be read at all. Dropping it
  // would silently clear an alias the operator never touched; leaving it
  // visible means the server gets to refuse it, in a sentence that says which
  // profile has to change — and, since it makes the box non-empty, it is also
  // what keeps "No replacement" reachable when there is nothing else to offer.
  const options =
    alias !== "" && !reachable.aliases.includes(alias)
      ? [alias, ...reachable.aliases]
      : reachable.aliases;
  // Disabled only when there is genuinely nothing to choose between: one
  // option and "No replacement" is still a choice, and a box locked onto a
  // prefilled alias would be a deprecation the operator cannot proceed with.
  const aliasDisabled = options.length === 0;

  const aliasHint = vocabularyPending
    ? "Reading the routing profiles to find out which aliases these callers can reach…"
    : vocabularyError !== null
      ? "The routing profiles could not be read, so there is no list to choose from. The change can still be recorded without a replacement."
      : !reachable.routed
        ? "No routing profile permits this model, so there is nobody to send anywhere and nothing to offer them."
        : options.length === 0
          ? "Every alias those profiles permit is served only by this model, so any replacement would send callers straight back here."
          : "Only aliases a profile permitting this model also permits, and that some other catalogue row serves there. It is what callers put on their next request, so it is an alias and never a provider's own model id.";

  const trimmedMessage = message.trim();
  const length = messageLength(trimmedMessage);
  const messageError =
    length > MAX_DEPRECATION_MESSAGE
      ? `${length} characters, and the limit is ${MAX_DEPRECATION_MESSAGE}. It is refused rather than trimmed: this text goes to callers verbatim, in a response header and an SSE frame, and half a sentence is worse than none.`
      : undefined;
  const aliasError = replacementRefusal(failure);
  const traffic = usageSentence(report, totals);

  const advice: ModelDeprecationAdvice = {};
  if (trimmedMessage !== "") {
    advice.deprecation_message = trimmedMessage;
  }
  if (alias !== "") {
    advice.replacement_alias = alias;
  }
  const sunsetAt = sunsetFromInput(sunset);
  if (sunsetAt !== undefined) {
    advice.sunset_at = sunsetAt;
  }
  const hasAdvice = Object.keys(advice).length > 0;
  // A date already in the past is accepted by the API — an operator recording a
  // sunset that has been and gone is stating a fact, not scheduling one — but
  // it is worth saying out loud, because the far more likely explanation is a
  // typo in the year.
  const sunsetDays = sunsetAt === undefined ? undefined : daysUntil(sunsetAt);
  const sunsetWarning =
    sunsetDays !== undefined && sunsetDays < 0
      ? `That date has already passed. Callers will be told the model stopped answering ${-sunsetDays} days ago.`
      : undefined;

  return (
    <div className="esm-subform">
      <h3>
        {label} {name}
      </h3>
      <p className="esm-muted-text">
        <code>{row.model}</code> on <code>{row.provider}</code>.{" "}
        {action?.description}
      </p>
      <Alert
        tone={totals === undefined ? "info" : "warning"}
        title="Who this reaches"
      >
        {traffic}
        {totals !== undefined && totals.aliases.length > 0
          ? ` Callers reach it as ${totals.aliases.join(", ")}.`
          : ""}
      </Alert>
      {/* Everything except the alias refusal, which is shown against the box
          the operator has to change. */}
      <ErrorAlert
        error={aliasError ? null : failure}
        title="That change was not saved"
      />
      <div className="esm-form-grid">
        <Field
          label="Message to callers"
          htmlFor="model-deprecation-message"
          hint={`Optional, up to ${MAX_DEPRECATION_MESSAGE} characters — ${length} so far. It is sent verbatim on every request that routes here, so write it for the developer who will read it in a log.`}
          error={messageError}
        >
          <textarea
            id="model-deprecation-message"
            className="esm-input esm-textarea"
            rows={3}
            value={message}
            onChange={(event) => {
              setMessage(event.target.value);
              setFailure(null);
            }}
          />
        </Field>
        <Field
          label="Replacement alias"
          htmlFor="model-replacement-alias"
          hint={aliasHint}
          error={aliasError}
        >
          <Select
            id="model-replacement-alias"
            value={alias}
            disabled={aliasDisabled}
            onChange={(event) => {
              setAlias(event.target.value);
              setFailure(null);
            }}
          >
            <option value="">No replacement &mdash; say nothing</option>
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Stops answering on"
          htmlFor="model-sunset-at"
          hint="Optional. Callers are told this date ahead of time — in a response header, in the chat stream and on their model list — so they can move before they are refused rather than after. Taken as midnight UTC at the start of the day."
        >
          <Input
            id="model-sunset-at"
            type="date"
            value={sunset}
            onChange={(event) => {
              setSunset(event.target.value);
              setFailure(null);
            }}
          />
        </Field>
      </div>
      {sunsetWarning === undefined ? null : (
        <Alert tone="warning" title="That date is in the past">
          {sunsetWarning}
        </Alert>
      )}
      <div className="esm-actions">
        <ReasonButton
          variant={action?.danger ? "danger" : "primary"}
          busy={setStatus.isPending}
          disabled={messageError !== undefined}
          mutation={{
            title: `${label} ${name}`,
            tone: action?.danger ? "danger" : "primary",
            confirmLabel: label,
            description: (
              <>
                <p>
                  <code>{row.model}</code> on <code>{row.provider}</code>.{" "}
                  {action?.description}
                </p>
                <p>{traffic}</p>
                <p>
                  {hasAdvice ? (
                    <>
                      They will be told:{" "}
                      {trimmedMessage === "" ? (
                        <em>nothing &mdash; no message was written</em>
                      ) : (
                        <q>{trimmedMessage}</q>
                      )}
                      {alias === "" ? (
                        ", with no replacement named"
                      ) : (
                        <>
                          , and pointed at <code>{alias}</code>
                        </>
                      )}
                      {sunsetAt === undefined ? (
                        "."
                      ) : (
                        <>
                          , counting down to{" "}
                          <strong>{sunsetLabel(sunsetAt)}</strong>.
                        </>
                      )}
                    </>
                  ) : (
                    "No message and no replacement are recorded, so callers are told only that the model changed status."
                  )}
                </p>
              </>
            ),
            run: async (reason) => {
              setFailure(null);
              try {
                const result = await setStatus.mutateAsync({
                  modelID: row.id,
                  status,
                  reason,
                  advice: hasAdvice ? advice : undefined,
                });
                onDone(
                  `${name} is now ${result.status}${
                    result.replacement_alias === null
                      ? ""
                      : `, pointing callers at ${result.replacement_alias}`
                  }${
                    result.sunset_at === null
                      ? ""
                      : `, stopping on ${sunsetLabel(result.sunset_at)}`
                  }.`,
                );
              } catch (error) {
                // Deliberately not rethrown. A rejection keeps the reason
                // dialog open with the refusal in a banner, which is the right
                // behaviour for a mutation whose form IS the dialog — but this
                // one's form is the panel underneath, and the alias refusal has
                // to land on the box that names the alias. Swallowing it closes
                // the dialog and puts the operator back on the field they have
                // to change, with the reason they typed still gone but nothing
                // else lost.
                setFailure(error);
              }
            },
          }}
        >
          {label}
        </ReasonButton>
        <Button
          variant="ghost"
          disabled={setStatus.isPending}
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * "Fetch what this provider offers, and take one."
 *
 * The whole point is to remove the typo in a model id. A provider model id is a
 * long, versioned, easily mistyped string — `claude-sonnet-4-5-20250929` — and
 * getting one character wrong produces a catalogue row that looks right, prices
 * fine, joins to a policy fine, and 404s the first time a customer calls it.
 * Picking from the vendor's own list makes that particular mistake impossible.
 *
 * What it CANNOT do is fill the form. Vendors differ enormously in how much
 * they publish: OpenRouter states a context length and what a model accepts,
 * Gemini states token limits, Anthropic states a name, OpenAI states an id. So
 * the ceilings and most capabilities are still the operator's to enter, and the
 * hint says so rather than leaving somebody to notice the boxes stayed empty.
 *
 * It is fetched ON DEMAND, never on open. This is the one control in the
 * console that makes the server call a third party, and a picker that reached
 * out every time the Add form appeared would be an upstream call for every
 * operator who opened it to correct a display name.
 */
function ModelPicker({
  provider,
  onPick,
}: {
  provider: string;
  onPick: (model: DiscoveredModel) => void;
}) {
  const accounts = useProviderAccounts();
  const [accountId, setAccountId] = useState("");
  const [filter, setFilter] = useState("");
  const available = useAvailableModels(
    accountId === "" ? undefined : accountId,
  );

  // Only accounts of the provider the form is set to, and only ones that could
  // actually answer: a suspended account or one with no credential produces a
  // refusal an operator would read as "this provider offers nothing".
  const usable = (accounts.data?.provider_accounts ?? []).filter(
    (account) =>
      account.provider === provider &&
      account.status === "active" &&
      account.active_credential_count > 0,
  );

  // The chosen account is cleared when the provider changes, or the picker
  // would keep showing an Anthropic catalogue under a form that now says
  // OpenAI.
  useEffect(() => {
    setAccountId("");
    setFilter("");
  }, [provider]);

  if (accounts.isPending) {
    return null;
  }
  if (usable.length === 0) {
    return (
      <Alert tone="info" title="No account to ask">
        Asking a provider what it offers needs one of its accounts with a live
        credential, and there is none for <code>{provider}</code> in this
        environment. Enter the model id by hand, or add the account first.
      </Alert>
    );
  }

  const models = available.data?.models ?? [];
  const needle = filter.trim().toLowerCase();
  const shown =
    needle === ""
      ? models
      : models.filter(
          (m) =>
            m.model.toLowerCase().includes(needle) ||
            m.display_name.toLowerCase().includes(needle),
        );

  return (
    <div className="esm-alias-block">
      <h3>Fetch from the provider</h3>
      <p className="esm-muted-text">
        Reads <code>{provider}</code>'s own catalogue through one of its
        accounts and fills in the model id. How much else arrives depends on the
        vendor — most publish little more than a name, so the token ceilings and
        capabilities below are usually still yours to enter.
      </p>
      <ErrorAlert
        error={available.error}
        title="That provider's catalogue could not be read"
      />
      <div className="esm-form-grid">
        <Field
          label="Ask through"
          htmlFor="model-picker-account"
          hint="The account whose credential is used for the call. Only active accounts with a live key are offered."
        >
          <Select
            id="model-picker-account"
            value={accountId}
            onChange={(event) => setAccountId(event.target.value)}
          >
            <option value="">Choose an account…</option>
            {usable.map((account) => (
              <option key={account.id} value={account.id}>
                {account.label} ({account.environment})
              </option>
            ))}
          </Select>
        </Field>
        {models.length > 0 ? (
          <Field
            label="Find a model"
            htmlFor="model-picker-filter"
            hint={`${models.length} offered. Type to narrow the list.`}
          >
            <Input
              id="model-picker-filter"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            />
          </Field>
        ) : null}
      </div>
      {available.isPending && accountId !== "" ? (
        <Spinner label={`Asking ${provider} what it offers`} />
      ) : null}
      {available.data && models.length === 0 ? (
        <p className="esm-muted-text">
          That account's credential worked and the provider named no models.
          Enter the id by hand.
        </p>
      ) : null}
      {shown.length > 0 ? (
        <DataTable<DiscoveredModel>
          rows={shown.slice(0, MAX_PICKER_ROWS)}
          rowKey={(row) => row.model}
          empty="Nothing matches."
          columns={[
            {
              key: "model",
              header: "Model",
              render: (row) => (
                <>
                  <div>
                    <code>{row.model}</code>
                  </div>
                  {row.display_name === row.model ? null : (
                    <span className="esm-muted-text">{row.display_name}</span>
                  )}
                </>
              ),
            },
            {
              key: "stated",
              header: "Stated by the provider",
              render: (row) => (
                <div className="esm-badge-row">
                  {row.context_window === null ? null : (
                    <Badge tone="neutral">
                      {stated(row.context_window)} context
                    </Badge>
                  )}
                  {row.capabilities.map((capability) => (
                    <Badge key={capability} tone="neutral">
                      {CAPABILITY_LABELS[capability as ModelCapabilityName] ??
                        capability}
                    </Badge>
                  ))}
                  {row.context_window === null &&
                  row.capabilities.length === 0 ? (
                    <span className="esm-not-recorded">
                      nothing beyond the name
                    </span>
                  ) : null}
                </div>
              ),
            },
            {
              key: "known",
              header: "",
              align: "end",
              // Marked, never hidden. An operator hunting for a model they
              // added last week has to be able to see it and see that it is
              // already there — a picker that silently omitted it would read
              // as the provider having dropped the model.
              render: (row) =>
                row.catalogued ? (
                  <StatusBadge status={row.status} />
                ) : (
                  <Button variant="secondary" onClick={() => onPick(row)}>
                    Use this
                  </Button>
                ),
            },
          ]}
        />
      ) : null}
      {shown.length > MAX_PICKER_ROWS ? (
        <p className="esm-muted-text">
          Showing the first {MAX_PICKER_ROWS} of {shown.length}. Narrow the list
          to find the rest — they are not lost, only unrendered.
        </p>
      ) : null}
    </div>
  );
}

/**
 * How many offered models the picker draws at once.
 *
 * OpenAI's catalogue is the reason there is a cap: it lists every model the
 * account can reach, embeddings and transcription included, which runs to
 * hundreds. Drawing them all makes a form somebody has to scroll past to reach
 * the boxes. The count of what was left out is stated, so a narrowed list is
 * obviously a narrowed list rather than the whole answer.
 */
const MAX_PICKER_ROWS = 25;
